import { useState, useRef, useEffect } from "react";
import Button from "@common/Button";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";

const SearchBar = ({ value = "", onChange, onSearch, userSlug }) => {
  const [open, setOpen] = useState(false);
  const [hi,   setHi]   = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const ref   = useRef();
  const timer = useRef();

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Suggestions come purely from the API, debounced
  useEffect(() => {
    const q = (typeof value === "string" ? value : "").trim().toLowerCase();
    if (q.length < 2 || !userSlug) { setSuggestions([]); return; }

    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const fd = new FormData();
        fd.append("keyword", q);
        fd.append("user_slug", userSlug);
        const res = await axios.post(`${API_BASE}get-autocomplete-main-result`, fd);
        const apiItems = res.data?.status === 1 ? (res.data.results || []) : [];
        setSuggestions(apiItems.slice(0, 8));
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }, [value, userSlug]);

  const pick = (item) => {
    const text = item.name || item.title || "";
    onChange(text);           // update display
    setSuggestions([]);
    setOpen(false);
    onSearch(text);           // pass directly so runSearch uses selected text not stale state
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--fc-text-subtle)", fontSize: 14 }} />
          <input
            value={value}
            onChange={e => { onChange(String(e.target.value || "")); setOpen(true); setHi(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === "Enter")     { if (hi >= 0 && suggestions[hi]) { pick(suggestions[hi]); } else { setOpen(false); onSearch(value); } }
              if (e.key === "ArrowDown") setHi(h => Math.min(h + 1, suggestions.length - 1));
              if (e.key === "ArrowUp")   setHi(h => Math.max(h - 1, -1));
              if (e.key === "Escape")    setOpen(false);
            }}
            placeholder="Search by name, role, skill or keyword..."
            style={{ width: "100%", padding: "10px 36px 10px 42px", border: "1px solid var(--fc-border)", borderRadius: 9, fontSize: 14, color: "var(--fc-text-primary)", outline: "none", background: "var(--fc-card-bg)", boxSizing: "border-box", transition: "border-color 0.2s" }}
            onFocus={e => e.target.style.borderColor = "var(--fc-primary)"}
            onBlur={e => e.target.style.borderColor = "var(--fc-border)"}
          />
          {value && (
            <button onClick={() => { onChange(""); setSuggestions([]); }} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--fc-text-subtle)", fontSize: 14 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
        <Button variant="primary" size="md" style={{ padding: "10px 24px", fontSize: 14, flexShrink: 0, borderRadius: 9 }} onClick={() => onSearch()}>
          Search
        </Button>
      </div>

      {open && suggestions.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 80, background: "var(--fc-card-bg)", border: "1px solid var(--fc-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 300, overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => pick(s)}
              style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", background: i === hi ? "var(--fc-primary-light)" : "var(--fc-card-bg)", color: i === hi ? "var(--fc-primary)" : "var(--fc-text-secondary)", borderBottom: i < suggestions.length - 1 ? "1px solid var(--fc-border-light)" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}
              onMouseEnter={() => setHi(i)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 10, color: "var(--fc-text-subtle)" }} />
                {s.name || s.title || ""}
              </div>
              {s.type && <span style={{ fontSize: 11, fontWeight: 600, color: "var(--fc-primary)", background: "var(--fc-primary-light)", padding: "2px 8px", borderRadius: 99 }}>{s.type}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;